import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, KeyRound, Building2, Save, QrCode, UploadCloud } from "lucide-react";

function ServiceToggles({ onChange }) {
  const [meta, setMeta] = useState({ service_types: [], all_service_types: [] });
  const [newSvc, setNewSvc] = useState("");
  const load = () => api.get("/meta").then((r) => setMeta(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (name, enabled) => {
    await api.post("/services/toggle", { service: name, enabled });
    await load();
    onChange && onChange();
  };
  const addCustom = async (e) => {
    e.preventDefault();
    if (!newSvc.trim()) return;
    await api.post("/services/custom", { name: newSvc.trim() });
    setNewSvc("");
    await load();
  };
  const enabledSet = new Set(meta.service_types || []);
  const preset = meta.all_service_types || [];
  const custom = (meta.service_types || []).filter((s) => !preset.includes(s));
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-2">
        {preset.map((s) => (
          <label key={s} className="flex items-center justify-between p-2 rounded-md border border-border">
            <span className="text-sm font-medium">{s}</span>
            <input type="checkbox" checked={enabledSet.has(s)} onChange={(e) => toggle(s, e.target.checked)} data-testid={`svc-toggle-${s}`} />
          </label>
        ))}
      </div>
      {custom.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Custom services</div>
          <div className="flex flex-wrap gap-2">
            {custom.map((c) => (
              <span key={c} className="badge badge-blue flex items-center gap-1">{c}
                <button className="text-red-600" onClick={async () => { await api.delete(`/services/custom/${encodeURIComponent(c)}`); load(); }} title="Remove">×</button>
              </span>
            ))}
          </div>
        </div>
      )}
      <form onSubmit={addCustom} className="flex gap-2">
        <input className="input flex-1" placeholder="Add custom service (e.g. FSSAI Registration)" value={newSvc} onChange={(e) => setNewSvc(e.target.value)} data-testid="svc-custom-input" />
        <button type="submit" className="btn btn-primary" data-testid="svc-custom-add">Add</button>
      </form>
    </div>
  );
}


export default function Settings() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const isAdmin = user?.role === "admin";
  const [settings, setSettings] = useState({ company_name: "", company_tagline: "", logo_url: "", company_qr_url: "", company_upi_id: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [msg, setMsg] = useState("");
  const [uploadingQr, setUploadingQr] = useState(false);

  const load = async () => {
    const r = await api.get("/settings");
    setSettings({ company_name: "", company_tagline: "", logo_url: "", company_qr_url: "", company_upi_id: "", ...r.data });
  };
  useEffect(() => { load(); }, []);

  const saveCompany = async (e) => {
    e.preventDefault();
    setMsg("");
    await api.post("/settings", {
      company_name: settings.company_name,
      company_tagline: settings.company_tagline,
      logo_url: settings.logo_url,
      company_upi_id: settings.company_upi_id,
    });
    setMsg("Company settings saved");
  };

  const changePw = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await api.post("/auth/change-password", pwForm);
      setPwForm({ current_password: "", new_password: "" });
      setMsg("Password changed successfully");
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Failed to change password");
    }
  };

  const uploadQr = async (file) => {
    if (!file) return;
    setUploadingQr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, company_qr_url: r.data.company_qr_url }));
      setMsg("QR code uploaded — connectors will now see it in Wallet");
    } catch (e) {
      setMsg("Failed to upload QR");
    } finally {
      setUploadingQr(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, logo_url: r.data.logo_url }));
      setMsg("Logo uploaded — refresh to see it on login & sidebar");
      window.dispatchEvent(new Event("logoChanged"));
    } catch { setMsg("Failed to upload logo"); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage company profile, payment QR, theme and password</p>
      </div>

      {msg && <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300 text-sm" data-testid="settings-message">{msg}</div>}

      {isAdmin && (
        <>
          <form onSubmit={saveCompany} className="card p-6 space-y-4" data-testid="company-settings-form">
            <div className="flex items-center gap-2">
              <Building2 size={18} />
              <h3 className="font-heading font-bold text-lg">Company Profile</h3>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Company Name</label>
              <input className="input mt-1" value={settings.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} data-testid="settings-company-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Tagline</label>
              <input className="input mt-1" value={settings.company_tagline || ""} onChange={(e) => setSettings({ ...settings, company_tagline: e.target.value })} data-testid="settings-tagline" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">UPI ID (shown next to QR)</label>
              <input className="input mt-1 font-mono" value={settings.company_upi_id || ""} onChange={(e) => setSettings({ ...settings, company_upi_id: e.target.value })} placeholder="yourupi@bank" data-testid="settings-upi-id" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Company Logo</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg border border-border overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                  {settings.logo_url ? (
                    <img src={`${backend}${settings.logo_url}`} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <img src="/nayan-logo.png" alt="Logo" className="w-full h-full object-cover" />
                  )}
                </div>
                <label className="btn btn-outline cursor-pointer inline-flex items-center gap-2" data-testid="settings-logo-upload">
                  <UploadCloud size={16} /> Upload Logo
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} />
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">PNG with transparent background recommended. Used on login page and sidebar.</p>
            </div>
            <button type="submit" className="btn btn-primary" data-testid="settings-save-btn"><Save size={16} /> Save</button>
          </form>

          <div className="card p-6 space-y-4" data-testid="qr-settings-card">
            <div className="flex items-center gap-2">
              <QrCode size={18} />
              <h3 className="font-heading font-bold text-lg">Payment QR Code</h3>
            </div>
            <p className="text-xs text-muted-foreground">Upload your company UPI QR image. Connectors will see this on their Wallet page and can scan it via any UPI app.</p>
            <div className="flex items-start gap-6 flex-wrap">
              <div className="w-40 h-40 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden">
                {settings.company_qr_url ? (
                  <img src={`${backend}${settings.company_qr_url}`} alt="Company QR" className="w-full h-full object-contain bg-white" data-testid="settings-qr-preview" />
                ) : (
                  <div className="text-xs text-muted-foreground text-center px-2">No QR uploaded</div>
                )}
              </div>
              <label className={`btn btn-primary cursor-pointer ${uploadingQr ? "opacity-60 pointer-events-none" : ""}`} data-testid="settings-upload-qr-btn">
                <UploadCloud size={16} /> {uploadingQr ? "Uploading…" : (settings.company_qr_url ? "Replace QR" : "Upload QR")}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; uploadQr(f); }} />
              </label>
              {settings.company_qr_url && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={async () => {
                    if (!window.confirm("Remove company QR? Connectors will no longer see it.")) return;
                    await api.delete("/settings/qr");
                    setSettings((s) => ({ ...s, company_qr_url: "" }));
                    setMsg("QR code removed");
                  }}
                  data-testid="settings-remove-qr-btn"
                >
                  Remove QR
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <div className="card p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Appearance</h3>
        <div className="flex items-center gap-3">
          <button className={`btn ${theme === "light" ? "btn-primary" : "btn-outline"}`} onClick={() => setTheme("light")} data-testid="theme-light-btn"><Sun size={16} /> Light</button>
          <button className={`btn ${theme === "dark" ? "btn-primary" : "btn-outline"}`} onClick={() => setTheme("dark")} data-testid="theme-dark-btn"><Moon size={16} /> Dark</button>
        </div>
      </div>

      {isAdmin && (
        <form onSubmit={changePw} className="card p-6 space-y-4" data-testid="password-form">
          <div className="flex items-center gap-2">
            <KeyRound size={18} />
            <h3 className="font-heading font-bold text-lg">Change Password</h3>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Current Password</label>
            <input required type="password" className="input mt-1" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="pw-current" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">New Password</label>
            <input required type="password" className="input mt-1" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="pw-new" />
          </div>
          <button type="submit" className="btn btn-primary" data-testid="pw-submit"><KeyRound size={16} /> Update Password</button>
        </form>
      )}

      {!isAdmin && (
        <div className="card p-6" data-testid="password-policy-notice">
          <div className="flex items-start gap-3">
            <KeyRound size={18} className="mt-0.5 text-primary" />
            <div>
              <h3 className="font-heading font-bold text-lg">Password Management</h3>
              <p className="text-sm text-muted-foreground mt-1">
                For security reasons, only the Administrator can reset your password.
                Please contact your admin if you need to change or reset it.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
